import {gql} from '@apollo/client';
import {captureException} from '@sentry/browser';
import {StyleSheet, css} from 'aphrodite';
import React, {ErrorInfo, ReactNode, useEffect, useRef, useState} from 'react';

import {StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import PrintableWrapper from 'notebook/mediocre/print/PrintableWrapper';
import {
  errorIndicatorAttrs,
  loadingIndicatorAttrs,
  waitForLoadingIndicatorsToResolve,
} from 'notebook/util/ExportInstrumentation';
import {getTypedStudyAdapter} from 'studies/typed/TypedStudyAdapters';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {
  StudyDesignPrintViewQuery,
  StudyDesignPrintViewQueryVariables,
} from './__generated/StudyDesignPrintViewQuery';

export const STUDY_DESIGN_PRINT_VIEW_QUERY = gql`
  query StudyDesignPrintViewQuery($studyId: StrictString!) @skipAudit @engComponent(name: STUDIES) {
    study(id: $studyId) {
      ... on StudyNode {
        id
        studySchema {
          ... on StudySchemaNode {
            id
            studyType
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

/**
 * Composite component that renders the study design in a printable format.
 *
 * This component can be shown to a user on its own page - it should not be nested in other components
 * due to its specialized handling of aphrodite sytyles. It can be used for user-initied brower print,
 * or server-side rendering that requires an HTML intermediary (nodeshell or headless chrome). See
 * {@link AphroditeWrapper} for more details.
 *
 * - PrintableWrapper to block on nested resources loading (e.g. <img> tags) and resize wide content.
 * - AphroditeWrapper to ensure that aphrodite CSS styles are included in the extracted HTML.
 * - ErrorBoundary to block that any rendering errors block server-side rendering jobs.
 * - InnerStudyDesignPrintView to fetch the study data and render the appropriate study adapter.
 */
export default function StudyDesignPrintView(props: {studyId: string}): JSX.Element {
  return (
    <PrintableWrapper title="Study Design">
      <AphroditeWrapper>
        <ErrorBoundary>
          <InnerStudyDesignPrintView studyId={props.studyId} />
        </ErrorBoundary>
      </AphroditeWrapper>
    </PrintableWrapper>
  );
}

/**
 * Fetches the minimal study data to then dispatch the appropriate study adapter for rendering.
 *
 * This component notably uses proper error handling and loading states to ensure that if the study design
 * is being rendered in a server-side context, the data is captured when it is fully loaded.
 */
function InnerStudyDesignPrintView(props: {studyId: string}): JSX.Element {
  const result = useBenchlingQuery<StudyDesignPrintViewQuery, StudyDesignPrintViewQueryVariables>(
    STUDY_DESIGN_PRINT_VIEW_QUERY,
    {variables: {studyId: props.studyId}}
  );
  switch (result.type) {
    case QueryResultType.PENDING:
      return <CenteredHorizontalThrobber />;
    case QueryResultType.SUCCESS: {
      if (isInaccessible(result.data.study)) {
        return (
          <ErrorBannerWithIndicator reason="inaccessible" queryResultType={result.type}>
            The study design is inaccessible.
          </ErrorBannerWithIndicator>
        );
      }
      const {studyType} = result.data.study.studySchema;
      const adapter = getTypedStudyAdapter(studyType);
      const {tab: StudyDesignTabContents} = adapter.design.ui;
      if (!StudyDesignTabContents) {
        return (
          <ErrorBannerWithIndicator
            reason="no-study-design-for-adapter"
            queryResultType={result.type}
            studyType={studyType}
          >
            The study design could not be displayed.
          </ErrorBannerWithIndicator>
        );
      }
      return <StudyDesignTabContents studyId={result.data.study.id} locked={true} viewer={true} />;
    }
    default:
      return (
        <ErrorBannerWithIndicator reason="network" queryResultType={result.type}>
          An error occurred while loading the study design.
        </ErrorBannerWithIndicator>
      );
  }
}

/**
 * A user-readable and machine-readable error banner.
 *
 * This component should be used anytime an error occurs when rendering the study design to ensure that any
 * server-side rendering jobs fail. {@link errorIndicatorAttrs} can be used for cases where the banner is not
 * appropriate.
 *
 * @param props.queryResultType - the graphql query result type. Captured in the HTML for debugging.
 * @param props.studyType - the type of the study that caused the error. Captured in the HTML for debugging.
 * @param props.reason - the reason for the error. Captured in the HTML for debugging.
 */
export function ErrorBannerWithIndicator(props: {
  queryResultType?: QueryResultType;
  studyType?: StudyType;
  reason: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <div
      // Signal that an error occurred.
      {...errorIndicatorAttrs()}
      // Attach some metadata in case we are able to capture the HTML for debugging.
      data-study-design-query-result-type={props.queryResultType}
      data-study-design-type={props.studyType}
      data-error-reason={props.reason}
      className={css(styles.errorBanner)}
    >
      <InlineBanner bannerType={InlineBannerType.ERROR} text={props.children} />
    </div>
  );
}

interface ErrorBoundaryState {
  hasError: boolean;
}

/**
 * Error boundary that captures errors and displays an error banner, with an error indicator.
 *
 * This component is necessary to ensure that a crash at render time in a child component does not simply
 * render nothing, but instead an error indicator that will fail any server-side rendering jobs.
 *
 * If we were to capture this failed HTML/PDF, the visual error indicator would be useful for debugging.
 *
 * TODO(BNCH-107024): It would be nice to have a positive indicator for when the study design is fully loaded,
 * so that this component serves primarily as a UX enhancement, esp. since a crash above this component
 * would still result in a blank page.
 */
class ErrorBoundary extends React.Component {
  public state: ErrorBoundaryState = {hasError: false};

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Update state so the next render will show the fallback UI.
    return {hasError: true};
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    captureException(error, {extra: {errorInfo}});
  }

  render(): ReactNode {
    return this.state.hasError ? (
      <ErrorBannerWithIndicator reason="js">
        An error occurred while rendering the study design.
      </ErrorBannerWithIndicator>
    ) : (
      this.props.children
    );
  }
}

/**
 * HTML-export-friendly wrapper that renders aphrodite <style> tag in the DOM.
 *
 * This component should be defensive against crashing as it is outside of the ErrorBoundary in
 * StudyDesignPrintView, primarily so we can render a nice error.
 *
 * As of 2024-09-27, our headless exports will render the page via Chrome, which render correctly , but then
 * serializes to HTML and extracts the relevant content. This two phase process was adopted to avoid a
 * memory issue... at the cost of aphrodite not working. This component provides a workaround to getting
 * aphrodite styles to be included in the extracted HTML by:
 *
 * 1) Rendering the <style> nested in the to-be-extracted <body> element (instead of the non-extracted <head>)
 * 2) Serializing the StyleSheet associated with <style> into is "innerHTML", which is otherwise just held
 *    in memory.
 *
 * In theory this component / strategy can work in nodeshell as well - as evidenced by functional unit tests.
 *
 * NOTE: this violates the HTML standard where the <style> tags should be in <head>, but all modern browsers
 * support <style> tags in the <body>, and is unlikely for this to change anytime soon.
 *
 * TODO(BNCH-107012): Remove once using JEF-based exports, which should support Aphrodite out-of-the-box.
 */
function AphroditeWrapper({children}: {children: React.ReactNode}): JSX.Element {
  const styleRef = useRef<HTMLStyleElement | null>(null);
  const [state, setState] = useState<{status: 'error' | 'loading'} | {status: 'done'; css: string}>({
    status: 'loading',
  });

  const contentRef = useRef<HTMLDivElement | null>(null);

  // Wait until the content is loaded, then serialize the stylesheet into the style tag.
  useEffect(() => {
    let isMounted = true;
    async function getCSS(): Promise<string | null> {
      if (!contentRef.current) {
        return null;
      }
      await waitForLoadingIndicatorsToResolve(contentRef.current);
      if (!styleRef.current?.sheet) {
        return null;
      }
      const css = Array.from(styleRef.current.sheet.cssRules)
        .map((rule) => rule.cssText)
        .join('\n');
      if (!css) {
        // Lack of styles is a bug since aphroditeSentinel should always result in CSS.
        return null;
      }
      return css;
    }
    getCSS().then((css) => {
      if (!isMounted) {
        return;
      }
      setState(css != null ? {status: 'done', css} : {status: 'error'});
    });
    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <div>
      <div className={css(styles.aphroditeSentinel)} />
      <style
        ref={styleRef}
        data-aphrodite={true}
        {...(state.status === 'loading' ? loadingIndicatorAttrs() : {})}
        {...(state.status === 'error' ? errorIndicatorAttrs() : {})}
        // eslint-disable-next-line react/no-danger -- <style> cannot have nested <script> tags.
        dangerouslySetInnerHTML={state.status === 'done' ? {__html: state.css} : undefined}
      />
      <div ref={contentRef}>{children}</div>
    </div>
  );
}

const styles = StyleSheet.create({
  aphroditeSentinel: {display: 'none'},
  errorBanner: {paddingTop: 20},
});
